import React from "react";
import { useFocusFirstInput } from "../hooks/useFocusFirstInput";
import { newID } from "../utils/unique";
import { DataAwareButton } from "./DataAwareButton";
import { useData } from "../hooks/useData";
import { useConditional } from "../hooks/useConditional";
import { EditableRegion } from "../form/EditableRegion";

export type ModalSize = "width-25vw" | "width-30vw" | "width-40vw" | "width-50vw" | "width-75vw" | "fullscreen" | "width-auto" | string;

export interface ModalProps {
    title?: string | any,
	editableTitle?: {
		obj: any,
		prop: string,
	}
	editableSubtitle?: {
		obj: any,
		prop: string,
	}
	titleCenter?: any,

    children?: any,
    size?: ModalSize,

    showCancel?: boolean,
	cancelTitle?: string,

    showSave?: boolean,
    saveTitle?: string,
	showDelete?: boolean,

	saveOnEnterKey?: boolean,
	cancelOnEscapeKey?: boolean,
	saveIsDataAware?: boolean,

	confirmOnDelete?: boolean, // enabled by default, unless === false

	style?: any,

	className?: string,

	buttonsLeft?: any,


    onCancel?: () => void,
    onSave?: () => void,
	onDelete?: () => void,
}

export function Modal(props: ModalProps) {

	const confirmDelete = useConditional(() => {
		return (
			<Modal
				title="Confirm Delete"
				size="width-25vw"
				saveTitle="Continue"
				onSave={props.onDelete}
				onCancel={confirmDelete.close}
				saveIsDataAware={false}
			>
				Are you sure you want to delete this item?
			</Modal>
		)
	});

    const buttons = [];
    if (props.showCancel !== false) {
        buttons.push(
            <button
                key="cancel"
                className="cancel"
                onClick={() => {
                    if (props.onCancel) props.onCancel();
                }}
            >{props.cancelTitle ? props.cancelTitle : "Cancel"}</button>
        );
    }
	if (props.showDelete === true) {
        buttons.push(
            <button
                key="delete"
				className="error"
                onClick={() => {
					if (props.confirmOnDelete === false) {
						if (props.onDelete) props.onDelete();
					} else {
						confirmDelete.show({});
					}
                }}
            >Delete</button>
        );
	}
    if (props.showSave !== false) {
		if (props.saveIsDataAware === false) {
			buttons.push(
				<button
					className="primary"
					key="save"
					onClick={() => {
						if (props.onSave) props.onSave();
					}}
				>{props.saveTitle ? props.saveTitle : "Save"}</button>
			);
	
		} else {
			buttons.push(
				<DataAwareButton
					key="save"
					onClick={() => {
						if (props.onSave) props.onSave();
					}}
				>{props.saveTitle ? props.saveTitle : "Save"}</DataAwareButton>
			);	
		}
    }
    const modalID = React.useRef(newID());
    const ref = React.useRef<any>();

    useFocusFirstInput(modalID.current);

    function handleKeyDown(e: any) {
        if ((props.saveOnEnterKey !== false) && e.code === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
            if (props.onSave) props.onSave();
			e.preventDefault();
			e.stopPropagation();

        } else if ((props.cancelOnEscapeKey !== false) && e.code === "Escape") {
            if (props.onCancel) props.onCancel();
			e.preventDefault();
			e.stopPropagation();

        }
	}

	const data = useData();
	// should we immediately trigger a validate to get validation state right and have the save button active if possible?
	if (data.validateCount <= 0) {
		// was any initial data given?
		if (data.value && Object.keys(data.value).length > 0) {
			data.validate();
		}
	}

	let title, subtitle;
	if (props.title) {
		title = <div className="font-large">{props.title}</div>;

	} else if (props.editableTitle) {
		title = (
			<EditableRegion
				intialHtml={props.editableTitle.obj[props.editableTitle.prop]}
				onChange={(e) => {
					props.editableTitle!.obj[props.editableTitle!.prop] = e.target.value;
				}}
			/>
		)

	}
	if (props.editableSubtitle) {
		subtitle = (
			<div className="tag">
				<EditableRegion
					style={{fontSize: "16px"}}
					intialHtml={props.editableSubtitle.obj[props.editableSubtitle.prop]}
					onChange={(e) => {
						props.editableSubtitle!.obj[props.editableSubtitle!.prop] = e.target.value;
					}}
				/>
			</div>
		)
		
	}

    return (
        <div className="modal-bg full-screen z-modal"
            onKeyDown={handleKeyDown}
            ref={ref}
            id={modalID.current}
        >

            <div
				className={`modal bg text-normal shadow-large ${props.size} ${(props.className ? props.className : "")}`}
				style={props.style}
			>

                <div className="title padding-10 flex horiz">
                    {title}
					{(title && subtitle) ? <div className="fill flex vert align-center">{props.titleCenter}</div> : undefined}
					{subtitle}
                </div>

                <div className="content padding-10">
                    {props.children}
                </div>

                <div className="buttons flex padding-10 gap-5">
					{props.buttonsLeft}
                    <div className="fill"></div>
                    {buttons}
                </div>

				{confirmDelete.render()}
            </div>

        </div>
    );
}